import {createSearchParams, useParams} from 'react-router-dom'
import {getRequest} from '../../../services/base'
import {useContext, useEffect, useMemo, useRef, useState} from 'react'
import {NavContext} from '../../../context/global_context'
import {DtcMappingValidationContext, SET_DTC_MAPPING_VALIDATION_LIST,} from './context/context'
import InputText from '../../common/InputText'
import DtcMappingValidationTable from './DtcMappingValidationTable'
import DtcValidationButton from '../../common/DtcValidationButton'
import {getSessionStorageData} from '../../../utils/SessionStorage'
import {downloadFileBlob} from '../../../utils/common'
import {Flex, Form, Row, Select, Space, Tooltip} from 'antd'
import {ExportOutlined} from '@ant-design/icons'
import useDebouncedValue from '../../../utils/common_function'
import {useIntl} from 'react-intl'
import { notifyError } from '../../../utils/notifications'

const DtcMappingValidationList = () => {
  const intl = useIntl()
  const { vehicle } = useParams()

  const [state, dispatch] = useContext(DtcMappingValidationContext)
  const [, navDispatch] = useContext(NavContext)

  const [search, setSearch] = useState('')
  const [pagination, setPagination] = useState({})
  const [versionOptions, setVersionOptions] = useState([])
  const [selectedVersion, setSelectedVersion] = useState(null)
  const [acronymOptions, setAcronymOptions] = useState([])
  const [selectedAcronym, setSelectedAcronym] = useState([])
  const [vinOptions, setVinOptions] = useState([])
  const [selectedVin, setSelectedVin] = useState([])
  const [isSupported, setIsSupported] = useState(true)
  const [isMissConfigured, setIsMissConfigured] = useState(false)
  const [isMissingComponentInGraph, setIsMissingComponentInGraph] =
    useState(false)
  const currentUrl = useRef(`/api/dtc_mapping_errors/?vehicle=${vehicle}`)

  const debouncedSearch = useDebouncedValue(search, 500)

  const vehicle_redirect_url = getSessionStorageData('vehicle')?.search
    ? `/vehicles/?${createSearchParams(getSessionStorageData('vehicle'))}`
    : `/vehicles`

  useEffect(() => {
    navDispatch({
      type: 'header',
      data: {
        header: intl.formatMessage({ id: 'layout.adminTools' }),
        sub_header:
          intl.formatMessage({ id: 'layout.vehicleList' }) +
          ` / ${vehicle} / ` +
          intl.formatMessage({ id: 'sidebar.dtcMappingValidation' }),
        id: vehicle,
        show_back_btn: true,
        dtc_details: [],
        back_btn_url: vehicle_redirect_url,
        full_width_container: false,
      },
    })
    getDtcMappingValidationVersions()
    getDtcMappingValidationVins()
    getDtcMappingValidationAcronyms()
  }, [])

  useEffect(() => {
    currentUrl.current = buildUrl({
      vehicle,
      search,
      data_version: selectedVersion || '',
      is_supported: isSupported ? 1 : 0,
      is_misconfigured: isMissConfigured ? 1 : 0,
      is_missing_component_in_graph: isMissingComponentInGraph ? 1 : 0,
      acronym: selectedAcronym,
      vin: selectedVin,
    })
    getDtcMappingValidationList()
  }, [
    debouncedSearch,
    selectedVersion,
    selectedAcronym,
    selectedVin,
    isSupported,
    isMissConfigured,
    isMissingComponentInGraph,
  ])

  const buildUrl = ({
    vehicle,
    search,
    data_version,
    is_supported,
    is_misconfigured,
    is_missing_component_in_graph,
    acronym,
    vin,
    limit,
    offset,
  }) => {
    const params = new URLSearchParams({
      vehicle,
      search,
      data_version,
      is_supported,
      is_misconfigured,
      is_missing_graph: 0,
      is_missing_component_in_graph,
    })
    if (acronym?.length) params.set('acronym', acronym.join(','))
    if (vin?.length) params.set('vin', vin.join(','))
    if (limit) params.set('limit', limit)
    if (offset) params.set('offset', offset)
    return `/api/dtc_mapping_errors/?${params.toString()}`
  }

  const getDtcMappingValidationList = async (url) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({
          id: 'dtcMappingValidation.fetchingData',
        }),
        showLoader: true,
      },
      notification: {},
    })

    currentUrl.current = url ? url : currentUrl.current
    let res = await getRequest(currentUrl.current)
    setPagination(res.pagination)
    dispatch({ type: SET_DTC_MAPPING_VALIDATION_LIST, data: res.results })

    navDispatch({
      type: 'loader',
      data: {
        showLoader: false,
      },
      notification: {},
    })

    // After the loader dispatch above: a 'loader' action overwrites `notification`,
    // so notifying before it would be wiped out in the same React batch.
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'dtcMappingValidation.loadError' }))
    }
  }

  const getDtcMappingValidationVersions = async () => {
    let res = await getRequest(
      `/api/dtc_mapping_errors/versions/?vehicle=${vehicle}`
    )
    setVersionOptions((res.data || []).map((val) => ({ value: val, label: val })))
  }

  const getDtcMappingValidationVins = async () => {
    let res = await getRequest(`/api/dtc_mapping_errors/vin/?vehicle=${vehicle}`)
    setVinOptions((res.data || []).map((val) => ({ value: val, label: val })))
  }

  const getDtcMappingValidationAcronyms = async () => {
    let res = await getRequest(
      `/api/dtc_mapping_errors/acronym/?vehicle=${vehicle}`
    )
    setAcronymOptions((res.data || []).map((val) => ({ value: val, label: val })))
  }

  const formatFilterUrl = (values) => {
    if (values?.length) {
      return values.join(',')
    }
    return ''
  }

  const exportDtcMappingValidation = async () => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'dtcMappingValidation.exporting' }),
        showLoader: true,
      },
      notification: {},
    })

    let res = await getRequest(
      `/api/dtc_mapping_errors/export/?vehicle=${vehicle}&search=${search}&data_version=${selectedVersion || ''}&is_supported=${isSupported ? 1 : 0}&is_misconfigured=${isMissConfigured ? 1 : 0}&is_missing_graph=0&is_missing_component_in_graph=${isMissingComponentInGraph ? 1 : 0}&acronym=${formatFilterUrl(selectedAcronym)}&vin=${formatFilterUrl(selectedVin)}`,
      true,
      true
    )
    navDispatch({
      type: 'loader',
      data: {
        showLoader: false,
      },
      notification: {},
    })
    // `res?.msg` is redundant: base.js sets status false on every failure,
    // and an error body is never a Blob.
    if (res?.status === false || !(res instanceof Blob)) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'dtcMappingValidation.exportError' }),
        },
      })
    } else {
      downloadFileBlob(res, `dtc_mapping_errors-${vehicle}.csv`)
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({
            id: 'dtcMappingValidation.exportSuccess',
          }),
        },
      })
    }
  }

  const dataSource = useMemo(() => {
    if (!state?.list?.length) return []

    return state?.list?.map((item, index) => ({
      ...item,
      key: `dtc-mapping-validation-${index}`,
      rowKey: `dtc-mapping-validation-${item.id}`,
      index: index,
    }))
  }, [state.list])

  return (
    <div className="page sub-page">
      <Row className="body">
        <Form className="full-width">
          <Flex
            gap={16}
            wrap={false}
            className="full-width"
            align="center"
          >
            <InputText
              name="search_dtc_mapping_validation"
              type="search"
              placeholder={intl.formatMessage({
                id: 'dtcMappingValidation.searchPlaceholder',
              })}
              value={search}
              isRequired={false}
              wrapperClass="search-dtc-mapping-validation"
              onChange={(value) => {
                setSearch(value)
              }}
            />

            <Select
              mode="multiple"
              placeholder={intl.formatMessage({
                id: 'dtcMappingValidation.selectAcronym',
              })}
              options={acronymOptions}
              value={selectedAcronym}
              onChange={(values) => setSelectedAcronym(values)}
              className='dtc-mapping-validation-select-acronym'
              allowClear
              showSearch
              maxTagCount="responsive"
              maxTagPlaceholder={(omittedValues) => (
                <Tooltip
                  title={omittedValues.map((v) => v.label).join(', ')}
                >
                  <span>+{omittedValues.length}</span>
                </Tooltip>
              )}
            />

            <Select
              placeholder={intl.formatMessage({
                id: 'dtcMappingValidation.selectVersion',
              })}
              options={versionOptions}
              value={selectedVersion}
              onChange={(value) => setSelectedVersion(value)}
              className='dtc-mapping-validation-select-version'
              allowClear
              showSearch
            />

            <Select
              mode="multiple"
              placeholder={intl.formatMessage({
                id: 'dtcMappingValidation.selectVin',
              })}
              options={vinOptions}
              value={selectedVin}
              onChange={(values) => setSelectedVin(values)}
              className='dtc-mapping-validation-select-vin'
              allowClear
              showSearch
              maxTagCount="responsive"
              maxTagPlaceholder={(omittedValues) => (
                <Tooltip
                  title={omittedValues.map((v) => v.label).join(', ')}
                >
                  <span>+{omittedValues.length}</span>
                </Tooltip>
              )}
            />

            <Space size="middle" align="center" style={{ marginLeft: 'auto' }}>
              <DtcValidationButton
                actionsLabel={intl.formatMessage({
                  id: 'dtcMappingValidation.actions',
                })}
                extraMenuItems={[
                  {
                    key: 'export',
                    icon: <ExportOutlined />,
                    label: intl.formatMessage({
                      id: 'dtcMappingValidation.export',
                    }),
                    onClick: exportDtcMappingValidation,
                  },
                ]}
              />
            </Space>
          </Flex>
        </Form>

        <Flex vertical gap={16} style={{ width: '100%' }}>
          <DtcMappingValidationTable
            dataSource={dataSource}
            pagination={pagination}
            getDtcMappingValidationList={getDtcMappingValidationList}
            isSupported={isSupported}
            isMissConfigured={isMissConfigured}
            isMissingComponentInGraph={isMissingComponentInGraph}
            onChangeIsSupported={setIsSupported}
            onChangeIsMissConfigured={setIsMissConfigured}
            onChangeIsMissingComponentInGraph={setIsMissingComponentInGraph}
          />
        </Flex>
      </Row>
    </div>
  )
}

export default DtcMappingValidationList
